import { getCookie, setCookie } from './index';

// Cookie names
const COOKIE_FEATURE_FLAG = 'enablePrivacyBannerSuppressionSPA';
const COOKIE_PAGE_VIEW_COUNT = 'UDNS_NTCS';

// Configuration
const PAGE_SHOW_THRESHOLD = 3;

/**
 * Check if transcend is available
 * @returns {boolean}
 */
const isTranscendAvailable = () => typeof window !== 'undefined' && window.transcend && typeof window.transcend.toggleConsentManager === 'function';

/**
 * Check if DNS banner is in opened state
 * @returns {boolean}
 */
const isDnsBannerOpened = () => typeof window !== 'undefined' && window.dnsBannerPosition?.status?.status === 'opened';

/**
 * Get current page view count from cookie
 * @returns {number}
 */
const getPageViewCount = () => {
  try {
    const currentValue = getCookie(COOKIE_PAGE_VIEW_COUNT);
    if (!currentValue) {
      return 0;
    }
    const parsed = parseInt(currentValue);
    // Handle NaN from non-numeric cookie values
    return Number.isNaN(parsed) ? 0 : parsed;
  } catch (error) {
    return 0;
  }
};

/**
 * Increment page view count
 * @param {number} currentCount
 */
const incrementPageViewCount = (currentCount) => {
  try {
    setCookie(COOKIE_PAGE_VIEW_COUNT, String(currentCount + 1));
  } catch (error) {
    console.debug('[Privacy Banner] Error incrementing page count:', error);
  }
};

/**
 * Suppress privacy banner by invoking transcend consent manager
 * @returns {boolean} - True if banner was suppressed successfully
 */
const suppressPrivacyBanner = () => {
  if (!isTranscendAvailable()) {
    return false;
  }

  try {
    window.transcend.toggleConsentManager();
    return true;
  } catch (error) {
    console.debug('[Privacy Banner] Error suppressing banner:', error);
    return false;
  }
};

/**
 * Handle privacy banner on internal routing (SPA navigation)
 *
 * Tracks page views and suppresses privacy banner after threshold:
 * - Only executes if enablePrivacyBannerSuppressionSPA cookie is true
 * - Only executes when DNS banner status is 'opened'
 * - Increments UDNS_NTCS page view counter (0→1→2→3)
 * - After 3 page views, suppresses privacy banner via transcend
 *
 * @returns {boolean} - True if privacy banner was suppressed, false otherwise
 */
export const handlePrivacyBannerOnRouting = () => {
  try {
    // Only execute if feature is enabled via cookie
    if (getCookie(COOKIE_FEATURE_FLAG) !== 'true') {
      return false;
    }

    // Only execute if DNS banner is opened
    if (!isDnsBannerOpened()) {
      return false;
    }

    const pageViewCount = getPageViewCount();

    // If page views less than threshold, increment counter
    if (pageViewCount < PAGE_SHOW_THRESHOLD) {
      incrementPageViewCount(pageViewCount);
      return false;
    }

    // Page views >= threshold, suppress privacy banner
    return suppressPrivacyBanner();
  } catch (error) {
    console.debug('[Privacy Banner] Error handling routing:', error);
    return false;
  }
};
